import type {
  FilledInputProps,
  InputLabelProps,
  InputProps,
  OutlinedInputProps,
} from '@mui/material'

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import TextInput from '@rentspree/component-2023.components.atoms.text-input'
import DatePicker from '@rentspree/component-2023.components.organisms.date-picker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'
import styled from 'styled-components'

dayjs.extend(utc)
export const InputDatePickerContainer = styled.div`
  .RSPDatePicker {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2101;

    margin: auto;
    height: fit-content;
  }

  input[type='date']::-webkit-inner-spin-button,
  input[type='date']::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
`
export const workaroundDatesOverride = {
  '.Mui-disabled': {
    color: '#bfbdbd !important',
  },
  display: 'block',
  '.MuiButton-textSecondary': {
    visibility: 'hidden',
  },
}

export const DatePickerOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  background-color: #0000002a;
  z-index: 2100;
`

export type DatePickerInputProps = {
  label: string
  name: string
  error?: boolean
  onDateChange: (value: string) => void
  value?: string
  inputProps?: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps>
  inputLabelProps?: Partial<InputLabelProps>
  helperText?: string
}

const yearMonthDayDate = (date: Date): string => {
  const dayjsDate = dayjs.utc(date)
  return dayjsDate.startOf('day').format('YYYY-MM-DD')
}

export const DatePickerInput: React.FC<DatePickerInputProps> = (props) => {
  const { label, name, error, onDateChange, value, inputProps, inputLabelProps, helperText } = props
  const [showDatePicker, setShowDatePicker] = useState(false)

  const handlePickerOnEvents = (newDate) => {
    onDateChange(yearMonthDayDate(newDate))
    setShowDatePicker(false)
  }

  const ClickableCalendarIcon = (
    <div
      onClick={() => setShowDatePicker(!showDatePicker)}
      style={{ cursor: 'pointer' }}
      role="button"
      aria-hidden="true"
      tabIndex={0}
    >
      <CalendarMonthIcon />
    </div>
  )

  return (
    <InputDatePickerContainer>
      <TextInput
        type="date"
        endAdornment={ClickableCalendarIcon}
        defaultValue=""
        value={value}
        label={label}
        name={name}
        error={error}
        helperText={helperText}
        InputLabelProps={{
          ...inputLabelProps,
          shrink: true,
        }}
        InputProps={{
          ...inputProps,
          role: 'textbox',
        }}
      />
      {showDatePicker && (
        <DatePicker
          sx={workaroundDatesOverride}
          variantAutoBreakpoint="sm"
          handleOnChange={handlePickerOnEvents}
          handleOnAddDate={handlePickerOnEvents}
          handleOnCancel={() => setShowDatePicker(false)}
          value={dayjs(value).toDate()}
          acceptButtonLabel="Save"
        />
      )}
      {showDatePicker && <DatePickerOverlay onClick={() => setShowDatePicker(false)} />}
    </InputDatePickerContainer>
  )
}
